import React, { useCallback, useMemo, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import axios from 'axios';
import type Decimal from 'decimal.js';
import { isEmpty, isNil, noop } from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { useStyles } from '~/assets/styles';
import {
  getAllLineItemColumns,
  type LineItemColumnId,
} from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/columnUtils';
import type {
  CoveredItemRow,
  InvoiceAnalysisResponse,
  InvoiceData,
  LineItemProps,
  LineItemRow,
  StatusType,
  ValueDescription,
} from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/types';
import {
  buildEntityDepreciationMap,
  calcCoveredAmount,
  computeCoveredAmountAfterConversion,
  EXCLUDED,
  getAggregateCoverages,
  isDepreciationApplicable,
  NO_COVERAGE,
  UNDECIDED,
} from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/utils';
import CardDialog from '~/components/CardDialog';
import mixpanel from '~/components/CmsMain/mixpanel';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { __PERMISSION_ACTIONS__ as PERMISSION_ACTIONS } from '~/components/core/Permissions/enums/PermissionActions';
import { __PERMISSION_VERBS__ as PERMISSION_VERBS } from '~/components/core/Permissions/enums/PermissionVerbs';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import type { COLORS } from '~/components/core/StatusChipCore';
import StatusChipCore from '~/components/core/StatusChipCore';
import SortableTable from '~/components/core/Tables/SortableTable';
import Heading from '~/components/core/TextComponents/Heading';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { useCms } from '~/components/hooks/useCms';
import type { DamageAssessmentConfig } from '~/components/hooks/useDamageAssessmentConfig';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import ArrowDownwardIcon from '~/components/icons/ArrowDownwardIcon';
import CloseIcon from '~/components/icons/CloseIcon';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import {
  COVERAGE_DECISIONS,
  type CoverageDecisionType,
  type SettlementParams,
  type SummaryItem,
} from '~/components/types/invoice-types';
import { CLIVE_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import type { Exposure } from '~/server_shared/swagger/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, reportAxiosError, reportErrorInProductionOrThrow, stringCmp } from '~/Utils';
import { safeDecimal } from '~/Utils/decimalUtils';
import { multiplyToCentsDecimal } from '~/Utils/toCents';

import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { LineItemsAmountSummary } from './components/LineItemsSummary';
import { Summary } from './components/Summary';
import { recalculateSummary } from './settlementUtils';

const generateId = uuidv4;

const statusToColorMap: Record<string, COLORS> = {
  success: 'green',
  error: 'red',
  warning: 'yellow',
  info: 'lightBlue',
};

interface LineItemsTableProps {
  lineItemsColumns: Array<{
    id: string;
    label?: string;
    specialCell?: (row: LineItemRow) => React.ReactNode;
  }>;
  currentLineItems: LineItemRow[];
  shouldShowAddItemButton: boolean;
  disabled: boolean;
  setCurrentLineItems: React.Dispatch<React.SetStateAction<LineItemRow[]>>;
  onLineItemClick?: (lineItem: LineItemRow) => void;
  handleLineItemClick?: (rowId: string) => void;
  shouldShowAmountSummary?: boolean;
  summaryItems?: SummaryItem[];
  invoiceDate?: string;
  policyCurrency?: string;
  lineItemsSectionTitle?: string;
  // Payment selection props
  customButtons?: React.ReactNode;
}

const LineItemsTable: React.FC<LineItemsTableProps> = ({
  lineItemsColumns,
  currentLineItems,
  shouldShowAddItemButton,
  disabled,
  setCurrentLineItems,
  onLineItemClick,
  handleLineItemClick,
  shouldShowAmountSummary,
  summaryItems,
  policyCurrency,
  customButtons,
  lineItemsSectionTitle,
}) => (
  <div className="max-h-screen w-full overflow-auto rounded-lg bg-white p-32 pt-20">
    <Heading variant={Heading.TYPES.H1} className="mb-8">
      {lineItemsSectionTitle || 'Line items'}
    </Heading>
    {(shouldShowAddItemButton || customButtons) && (
      <div className="mb-8 inline-flex w-full justify-end gap-8">
        {customButtons || (
          <>
            {shouldShowAddItemButton && (
              <Button
                color="primary"
                disabled={disabled}
                onClick={() => {
                  mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_INVOICE_PAGE_LINE_ITEM_ADD_ROW_CLICKED);

                  setCurrentLineItems((previousRows) =>
                    previousRows.concat({
                      id: generateId(),
                      order: Math.max(...previousRows.map(({ order }) => order)),
                      amount_currency: policyCurrency,
                    })
                  );
                }}
              >
                <AddIcon />
                Add Item
              </Button>
            )}
          </>
        )}
      </div>
    )}
    <SortableTable
      columns={lineItemsColumns}
      rows={currentLineItems}
      stickyHeader
      onRowClick={onLineItemClick ? handleLineItemClick : undefined}
      keepRowsOrder
    />
    {shouldShowAmountSummary && summaryItems && summaryItems.length > 0 && (
      <LineItemsAmountSummary items={summaryItems} />
    )}
  </div>
);

const AmountSpecialCell: React.FC<{ amount: number }> = ({ amount }) => {
  const { currencyFormatter } = useCurrencyFormatter();
  return <>{currencyFormatter.format(amount || 0)}</>;
};

interface onUpdateProps {
  status: StatusType;
  message?: string;
}

interface ApplyButtonProps {
  row: CoveredItemRow;
  onApply: (item: CoveredItemRow, onUpdate: (data: onUpdateProps) => void) => Promise<void>;
  coverageDecisionMap: Record<CoverageDecisionType, string>;
  exposure?: Exposure;
  disabled: boolean;
  saveDisabledMessage?: string;
}

export const ApplyButton: React.FC<ApplyButtonProps> = ({
  row,
  onApply,
  coverageDecisionMap,
  exposure,
  disabled,
  saveDisabledMessage,
}) => {
  const { userOrganization } = useCms();
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<StatusType>('initial');
  const [message, setStatusMessage] = useState<string>('');
  const { currencyFormatter } = useCurrencyFormatter();
  const werePaymentsMade = exposure?.indemnity?.paid_sum !== undefined && exposure?.indemnity?.paid_sum !== 0;
  const isReserveInvalid = exposure?.indemnity?.reserve !== undefined && exposure?.indemnity?.reserve !== 0;
  const wasDeductibleApplied = !isNil(exposure?.deductible_amount) && exposure.deductible_amount > 0;
  const hasCoverageIssue = exposure?.is_coverage_issue_exists;
  const isIgnoreReserveEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.INVOICE_QUICK_PAYMENT_IGNORE_RESERVE
  );

  const paymentValidations: Array<{ condition: boolean; message: string }> = [
    { condition: hasCoverageIssue ?? false, message: 'Coverage issue exists' },
    { condition: !row.amounts.covered, message: 'Covered amount is not set' },
    { condition: werePaymentsMade, message: 'Payment has already been made for this coverage' },
    { condition: wasDeductibleApplied, message: 'A deductible has already been applied to this coverage' },
    {
      condition: isReserveInvalid && !isIgnoreReserveEnabled,
      message: `An exposure exists for this coverage, but the reserve (${currencyFormatter.format(exposure?.indemnity?.reserve || 0)}) is not ${currencyFormatter.format(0)}`,
    },
  ];

  const paymentNotAllowedMessage =
    saveDisabledMessage || paymentValidations.find(({ condition }) => condition)?.message || '';
  const isPaymentAllowed = !paymentNotAllowedMessage;

  const onUpdate = ({ status, message }: { status: StatusType; message?: string }) => {
    setStatus(status);
    setStatusMessage(message ?? '');
  };

  const handleOnClick = async () => {
    setSubmitting(true);
    await onApply(
      {
        id: row.id,
        coverage: row.id,
        amounts: Object.fromEntries(
          Object.entries(row.amounts).filter(([key]) => Object.keys(coverageDecisionMap).includes(key))
        ),
      },
      onUpdate
    );
    setSubmitting(false);
  };

  return status === 'initial' ? (
    <Tooltip title={paymentNotAllowedMessage} disabled={isPaymentAllowed}>
      <span>
        <Button
          key={row.id}
          disabled={disabled || submitting || !isPaymentAllowed}
          color="primary"
          onClick={handleOnClick}
        >
          Pay
        </Button>
      </span>
    </Tooltip>
  ) : (
    <Tooltip key={row.id} title={message}>
      <span>
        <StatusChipCore status={status} color={statusToColorMap[status]} />
      </span>
    </Tooltip>
  );
};

export interface InvoiceRecommendationAnalysisProps {
  coverages?: ValueDescription[];
  onClose: () => void;
  onSave: (data: InvoiceAnalysisResponse) => Promise<boolean>;
  onSaveAndFinish?: (data: InvoiceAnalysisResponse) => Promise<boolean>;
  onApplyCoveredItem?: (
    item: CoveredItemRow,
    onUpdate: (data: { message?: string; status: StatusType }) => void
  ) => Promise<void>;
  invoiceData?: InvoiceData;
  lineItems?: LineItemProps[];
  exposuresByCoverageKey?: Record<string, NonNullable<ClaimPolymorph['exposures']>[number]>;
  filePath?: string;
  dialogTitle?: string;
  open: boolean;
  disabled: boolean;
  submitting: boolean;
  isLoading?: boolean;
  isError?: boolean;
  coInsuranceCoverageRates: Record<string, number>;
  lineItemColumnsToShow?: LineItemColumnId[];
  shouldShowCoverageSummary?: boolean;
  validateBeforeSave?: (lineItems: LineItemRow[]) => string | undefined;
  entities?: ValueDescription[];
  possibleCurrencies?: Array<{ value: string; label: string }>;
  onLineItemClick?: (lineItem: LineItemRow) => void;
  policyCurrency?: string;
  claim?: ClaimPolymorph;
  shouldShowInvoiceSummary?: boolean;
  customMainTitle?: string;
  shouldShowAddItemButton?: boolean;
  lineItemsSectionTitle?: string;
  onDepreciationClick?: (lineItem: LineItemRow) => Promise<LineItemRow | undefined>;
  onCalculateAllDepreciations?: (currentLineItems: LineItemRow[]) => Promise<LineItemRow[]>;
  isCalculatingDepreciation?: boolean;
  shouldShowLineItemsAmountSummary?: boolean;
  summaryItems?: SummaryItem[];
  settlementParams?: SettlementParams | null;
  columnLabelOverrides?: Partial<Record<string, string>>;
  shouldAutoHandleMultiCurrencyColumns?: boolean;
  shouldShowDatePerLineItem?: boolean;
  // Payment selection props
  isPaymentSelectionMode?: boolean;
  selectedPayableType?: 'expenses' | 'indemnity' | null;
  selectedLineItems?: Set<string>;
  onSelectionChange?: (itemId: string, selected: boolean) => void;
  customButtons?:
    | React.ReactNode
    | ((
        getCurrentData: () => InvoiceAnalysisResponse,
        helpers: {
          handleCalculateAllDepreciations?: () => Promise<void>;
          isCalculatingDepreciation?: boolean;
          shouldShowDepreciationButton?: boolean;
        }
      ) => React.ReactNode);
  shouldReloadItemsAfterInit?: boolean;
  /**
   * Rows the parent wants merged into the editable line items without going through the
   * `lineItems` reseed path. New ids are appended once after init; ids already present in
   * the editable state are left alone, so user edits to them survive subsequent appends.
   */
  extraPendingRows?: LineItemProps[];
  isInvoiceGranularity?: boolean;
  onCloseFilePath?: () => void;
  /**
   * Per-row payable_type behavior driven by each row's exposure DA config.
   * When `payableConfigGuardActive` is true, the payable_type column renders per row:
   * exposure with allowed_payable_types='indemnity'/'expenses' → read-only label;
   * 'both'/null → today's 2-option dropdown; no exposure_id picked yet → disabled cell.
   * When false (default for orgs without any DA configuring allowed_payable_types, and
   * for ODIE mode), the column behaves identically to today.
   */
  exposureToDAConfig?: Map<number, DamageAssessmentConfig>;
  payableConfigGuardActive?: boolean;
}

export const InvoiceRecommendationAnalysis: React.FC<InvoiceRecommendationAnalysisProps> = ({
  coverages = [],
  lineItems = [],
  invoiceData = {},
  exposuresByCoverageKey = {},
  coInsuranceCoverageRates = {},
  filePath,
  onClose = noop,
  onSave = () => Promise.resolve(false),
  onSaveAndFinish,
  onApplyCoveredItem = () => Promise.resolve(),
  open = false,
  disabled = false,
  submitting = false,
  isLoading = false,
  isError = false,
  dialogTitle,
  lineItemColumnsToShow = [
    'description',
    'amount',
    'coverage_decision',
    'coverage',
    'covered_amount',
    'explanation',
    'actions',
  ],
  shouldShowCoverageSummary = true,
  validateBeforeSave,
  entities,
  onLineItemClick,
  policyCurrency,
  claim,
  shouldShowInvoiceSummary = true,
  customMainTitle,
  shouldShowAddItemButton = true,
  lineItemsSectionTitle,
  onDepreciationClick,
  onCalculateAllDepreciations,
  isCalculatingDepreciation = false,
  shouldShowLineItemsAmountSummary = false,
  summaryItems: summaryItemsProp,
  settlementParams: settlementParamsProp = null,
  columnLabelOverrides,
  shouldAutoHandleMultiCurrencyColumns = false,
  shouldShowDatePerLineItem = false,
  // Payment selection props
  isPaymentSelectionMode = false,
  selectedPayableType = null,
  selectedLineItems = new Set(),
  onSelectionChange,
  customButtons,
  shouldReloadItemsAfterInit = false,
  extraPendingRows,
  isInvoiceGranularity = false,
  onCloseFilePath,
  exposureToDAConfig,
  payableConfigGuardActive = false,
}) => {
  const [convertingLineItemId, setConvertingLineItemId] = useState<string | null>(null);
  const { userOrganization } = useCms();

  // Check permission to override depreciation calculations
  const canOverrideDepreciation = useHasPermission({
    action: PERMISSION_ACTIONS.OVERRIDE_DEPRECIATION_CALCULATIONS,
    verb: PERMISSION_VERBS.WRITE,
  });

  const initialLineItems = useMemo(
    () =>
      lineItems.map((row, index) => ({
        ...row,
        id: row.id || generateId(),
        order: index,
        covered_amount: calcCoveredAmount(row, row.covered_amount ?? row.amount),
      })),
    [lineItems]
  );
  const [currentLineItems, setCurrentLineItems] = useState<LineItemRow[]>(initialLineItems);
  const [currentInvoiceData, setCurrentInvoiceData] = useState<InvoiceData>(invoiceData);

  const hasMultiCurrencyLineItems = useMemo(() => {
    if (!policyCurrency || !shouldAutoHandleMultiCurrencyColumns) return false;
    return currentLineItems.some((item) => item.amount_currency && item.amount_currency !== policyCurrency);
  }, [currentLineItems, policyCurrency, shouldAutoHandleMultiCurrencyColumns]);

  const effectiveEntities = entities || coverages;

  // Per-exposure depreciation flag, keyed by exposure id (shared builder with columnUtils). Used
  // to skip re-deriving covered_amount on conversion for depreciation-driven rows.
  const entityDepreciationMap = useMemo(() => buildEntityDepreciationMap(effectiveEntities), [effectiveEntities]);

  // Check if depreciation is supported AND there are indemnity line items
  const shouldShowDepreciationColumns = useMemo(() => {
    const isSupported =
      effectiveEntities.some((entity) => entity.damage_assessment_include_depreciation) &&
      onDepreciationClick !== undefined;
    return isSupported && currentLineItems.some((item) => item.payable_type === 'indemnity');
  }, [effectiveEntities, onDepreciationClick, currentLineItems]);

  const effectiveLineItemColumnsToShow = useMemo(() => {
    let cols = [...lineItemColumnsToShow];

    // Filter out depreciation columns if not applicable
    if (!shouldShowDepreciationColumns) {
      const depreciationCols: LineItemColumnId[] = ['category', 'depreciation', 'months_owned'];
      cols = cols.filter((col) => !depreciationCols.includes(col));
    }

    if (!hasMultiCurrencyLineItems) {
      return cols;
    }
    const conversionCols: LineItemColumnId[] = [
      'currency_conversion',
      'conversion_date',
      'conversion_rate',
      'base_amount',
    ];
    const missingCols = conversionCols.filter((col) => !cols.includes(col));

    if (missingCols.length > 0) {
      const firstDepreciationColIdx = cols.findIndex((c) => ['category', 'depreciation', 'months_owned'].includes(c));

      let targetIdx = firstDepreciationColIdx;

      if (targetIdx === -1) {
        targetIdx = cols.indexOf('covered_amount');
      }

      if (targetIdx === -1) {
        targetIdx = cols.indexOf('actions');
      }

      if (targetIdx !== -1) {
        cols.splice(targetIdx, 0, ...missingCols);
      } else {
        cols.push(...missingCols);
      }
    }

    return cols;
  }, [lineItemColumnsToShow, hasMultiCurrencyLineItems, shouldShowDepreciationColumns]);

  const [editedInvoiceFields, setEditedInvoiceFields] = useState<Partial<InvoiceData>>({
    date: invoiceData.date ? moment(invoiceData.date).format('YYYY-MM-DD') : undefined,
    number: invoiceData.number ?? '',
    vendor: invoiceData.vendor ?? '',
  });
  const [isFileLoaded, setIsFileLoaded] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const handleLineItemClick = React.useCallback(
    (rowId: string) => {
      if (onLineItemClick) {
        const row = currentLineItems.find((row) => row.id === rowId);
        if (row) {
          onLineItemClick(row);
        }
      }
    },
    [onLineItemClick, currentLineItems]
  );

  const handleCurrencyConversionClick = useCallback(
    async (row: LineItemRow) => {
      if (!claim || !policyCurrency || !userOrganization?.id) {
        return;
      }

      try {
        setConvertingLineItemId(row.id);

        const itemDate = row.item_date;
        if (!itemDate) {
          reportErrorInProductionOrThrow('Invoice date not found for line item');
          return;
        }

        const conversionDate = moment(itemDate).format('YYYY-MM-DD');
        const itemCurrency = row.amount_currency;

        if (!itemCurrency) {
          reportErrorInProductionOrThrow('item currency not found');
          return;
        }

        // Call currency conversion API to get exchange rate
        const response = await axios.get(
          `/api/v1/currency_conversion/organizations/${userOrganization.id}/exchange_rates`,
          {
            params: {
              date: conversionDate,
              base_currency: itemCurrency,
              currencies: policyCurrency,
            },
          }
        );

        const conversionRate = response.data.rates[policyCurrency];

        if (!conversionRate) {
          // eslint-disable-next-line no-console
          console.error('Conversion rate not found in response');
          return;
        }

        // Quantize the DECIMAL product at the source (a float product loses exact half-cents
        // before rounding): the frontend sends cents, so what the user sees pre-save is
        // exactly what will be stored.
        const baseAmount = row.amount ? multiplyToCentsDecimal(row.amount, conversionRate).toNumber() : undefined;

        // Update the row with conversion data and, on the *first* conversion, re-derive
        // covered_amount from the converted figures. covered_amount is denominated in the policy
        // currency, so the first apply must refresh it — otherwise a coverage decision made before
        // conversion leaves a stale foreign figure that inflates settlement and payment totals. A
        // re-apply keeps the existing covered_amount, which is already policy-currency and may hold
        // a deliberate manual partial-coverage edit.
        setCurrentLineItems((prevLineItems) =>
          prevLineItems.map((lineItem) => {
            if (lineItem.id !== row.id) {
              return lineItem;
            }
            const wasAlreadyConverted = Boolean(lineItem.base_amount);
            const convertedItem = {
              ...lineItem,
              conversion_date: conversionDate,
              conversion_rate: conversionRate,
              base_amount: baseAmount,
            };
            const hasDepreciation = isDepreciationApplicable(convertedItem, entityDepreciationMap);
            return {
              ...convertedItem,
              covered_amount: computeCoveredAmountAfterConversion(convertedItem, hasDepreciation, wasAlreadyConverted),
            };
          })
        );
      } catch (error) {
        reportAxiosError(error);
      } finally {
        setConvertingLineItemId(null);
      }
    },
    [claim, policyCurrency, userOrganization, entityDepreciationMap]
  );

  // Initialize invoice data only once
  React.useEffect(() => {
    if (!isLoading && !hasInitialized) {
      setCurrentInvoiceData(invoiceData);
      setEditedInvoiceFields({
        date: invoiceData.date ? moment(invoiceData.date).format('YYYY-MM-DD') : undefined,
        number: invoiceData.number ?? '',
        vendor: invoiceData.vendor ?? '',
      });
    }
  }, [invoiceData, isLoading, hasInitialized]);

  // Keep `row.payable_type` consistent with the cell's forced value when the per-row
  // guard is active and the row's exposure DA config forces indemnity/expenses.
  // Returns the same row reference when no change is needed (avoids spurious re-renders).
  const applyForcedPayable = useCallback(
    (row: LineItemRow): LineItemRow => {
      if (!payableConfigGuardActive || !row.exposure_id) return row;
      const allowed = exposureToDAConfig?.get(row.exposure_id)?.allowed_payable_types;
      if ((allowed === 'indemnity' || allowed === 'expenses') && row.payable_type !== allowed) {
        return { ...row, payable_type: allowed };
      }
      return row;
    },
    [payableConfigGuardActive, exposureToDAConfig]
  );

  // Initialize line items once, and update fields when data reloads
  React.useEffect(() => {
    if (!isLoading && (!hasInitialized || shouldReloadItemsAfterInit)) {
      setCurrentLineItems(initialLineItems.map(applyForcedPayable));
      setHasInitialized(true);
    }
  }, [initialLineItems, isLoading, hasInitialized, shouldReloadItemsAfterInit, applyForcedPayable]);

  // Safety net for DA configs that change AFTER the line items are already in state
  // (e.g. background refetch after a DA config edit). Init / pending-merge / updateRow
  // already apply applyForcedPayable inline, so this effect is a no-op on first render.
  // Idempotent — only writes back when at least one row's payable changed.
  React.useEffect(() => {
    setCurrentLineItems((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        const synced = applyForcedPayable(row);
        if (synced !== row) changed = true;
        return synced;
      });
      return changed ? next : prev;
    });
  }, [applyForcedPayable]);

  // Append any new ids from extraPendingRows after init. We diff against the live editable
  // state (not a ref of seen ids), so the post-save reseed wipes pending rows and the
  // parent then clears extraPendingRows in the same cycle — no stale-merge to worry about.
  React.useEffect(() => {
    if (!hasInitialized || !extraPendingRows || extraPendingRows.length === 0) return;
    setCurrentLineItems((prev) => {
      const existingIds = new Set(prev.map((r) => r.id));
      const newRows = extraPendingRows.filter((r) => r.id && !existingIds.has(r.id));
      if (newRows.length === 0) return prev;
      const baseOrder = prev.length;
      return [
        ...prev,
        ...newRows.map((row, i) =>
          applyForcedPayable({
            ...row,
            id: row.id || generateId(),
            order: baseOrder + i,
            covered_amount: calcCoveredAmount(row, row.covered_amount ?? row.amount),
          })
        ),
      ];
    });
  }, [extraPendingRows, hasInitialized, applyForcedPayable]);

  const { classes } = useStyles();
  const updateRow = (row: LineItemRow, field: string, value: string | number | boolean | undefined) => {
    let updatedRow;
    setCurrentLineItems((prevState) =>
      prevState.map((currentRow) => {
        if (currentRow.id !== row.id) {
          return currentRow;
        }
        // Re-sync forced payable when exposure_id changes (no-op when guard inactive).
        // applyForcedPayable returns the same reference when nothing needs to change,
        // so this is safe for non-exposure-id field changes too.
        updatedRow = applyForcedPayable({ ...row, [field]: value });
        return updatedRow;
      })
    );
    return updatedRow;
  };

  const mixpanelFocus = useCallback((field: string) => {
    mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_INVOICE_PAGE_LINE_ITEM_FOCUSED, {
      focus: 'on',
      field,
    });
  }, []);

  const mixpanelBlur = useCallback((field: string) => {
    mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_INVOICE_PAGE_LINE_ITEM_FOCUSED, {
      focus: 'off',
      field,
    });
  }, []);

  const anyLineItemHasExplanation = currentLineItems.some((row) => row.explanation && row.explanation !== '-');

  // Summary items: recalculate from current (possibly edited) line items using backend-provided params.
  const effectiveSummaryItems: SummaryItem[] = React.useMemo(() => {
    if (!shouldShowLineItemsAmountSummary) return [];

    if (settlementParamsProp) {
      return recalculateSummary(currentLineItems, settlementParamsProp);
    }

    if (summaryItemsProp && summaryItemsProp.length > 0) {
      return summaryItemsProp;
    }

    return [];
  }, [currentLineItems, shouldShowLineItemsAmountSummary, summaryItemsProp, settlementParamsProp]);

  const allLineItemColumns = getAllLineItemColumns({
    entities: effectiveEntities,
    disabled,
    anyLineItemHasExplanation,
    updateRow,
    setCurrentLineItems,
    mixpanelFocus,
    mixpanelBlur,
    classes,
    claim,
    onCurrencyConversionClick: handleCurrencyConversionClick,
    onDepreciationClick,
    policyCurrency,
    convertingLineItemId,
    isCalculatingDepreciation,
    canOverrideDepreciation,
    // Payment selection parameters
    isPaymentSelectionMode,
    selectedPayableType,
    selectedLineItems,
    onSelectionChange,
    columnLabelOverrides,
    isInvoiceGranularity,
    exposureToDAConfig,
    payableConfigGuardActive,
  });

  const lineItemsColumns = effectiveLineItemColumnsToShow.map((id) => allLineItemColumns[id]).filter(Boolean);

  const aggregateCoverages = getAggregateCoverages(currentLineItems);

  const saveDisabledMessage = validateBeforeSave ? validateBeforeSave(currentLineItems) : undefined;

  // Provide a clean way for custom buttons to access current data
  const getCurrentData = useCallback(
    (): InvoiceAnalysisResponse => ({
      line_items: currentLineItems,
      coverages: aggregateCoverages,
      invoice_data: currentInvoiceData,
    }),
    [currentLineItems, aggregateCoverages, currentInvoiceData]
  );

  // Wrapped handler for calculating all depreciations (updates internal state)
  const handleCalculateAllDepreciations = useCallback(async () => {
    if (!onCalculateAllDepreciations) return;
    const updatedItems = await onCalculateAllDepreciations(currentLineItems);
    setCurrentLineItems(updatedItems);
  }, [onCalculateAllDepreciations, currentLineItems]);

  // Resolve customButtons - if it's a function, call it with getCurrentData and helpers
  const resolvedCustomButtons =
    typeof customButtons === 'function'
      ? customButtons(getCurrentData, {
          handleCalculateAllDepreciations: onCalculateAllDepreciations ? handleCalculateAllDepreciations : undefined,
          isCalculatingDepreciation,
          shouldShowDepreciationButton: shouldShowDepreciationColumns,
        })
      : customButtons;

  // COINSURANCE_ROUNDING: the product is quantized in Decimal, exactly like the backend's
  // (amount * rate).quantize(0.01, ROUND_HALF_UP) in calculate_settlement. Quantizing the
  // float product instead loses the half cent before it can round
  // (100.10 * 0.75 === 75.07499999999999 -> 75.07 where the backend returns 75.08).
  const postCoInsuranceCalc = (amount: number, coverage: string): Decimal =>
    multiplyToCentsDecimal(amount, coInsuranceCoverageRates[coverage] || 1);

  const coverageDecisionMap = COVERAGE_DECISIONS.reduce(
    (acc, { value, description }) => {
      acc[value] = description;
      return acc;
    },
    {} as Record<string, string>
  );

  const coInsuranceColumns =
    isEmpty(coInsuranceCoverageRates) || Object.values(coInsuranceCoverageRates).every((rate) => rate === 1)
      ? []
      : [
          {
            id: 'co_insurance',
            label: 'Co-Insurance',
            specialCell: (row: CoveredItemRow) => {
              if (row.coverage === EXCLUDED) {
                return undefined;
              }

              const amount = row.amounts.covered;
              // Exact Decimal subtraction, like the backend's co_insurance = subtotal - eligible;
              // both operands are cent-clean, so the result is too.
              return amount !== undefined ? (
                <AmountSpecialCell
                  amount={safeDecimal(amount).minus(postCoInsuranceCalc(amount, row.coverage)).toNumber()}
                />
              ) : (
                ''
              );
            },
          },
          {
            id: 'eligible_for_reimbursement',
            label: 'Eligible For Reimbursement',
            specialCell: (row: CoveredItemRow) => {
              if (row.coverage === EXCLUDED) {
                return undefined;
              }

              const amount = row.amounts.covered;
              return amount !== undefined ? (
                <AmountSpecialCell amount={postCoInsuranceCalc(amount, row.coverage).toNumber()} />
              ) : (
                ''
              );
            },
          },
        ];

  const coverageSummaryColumns = [
    {
      id: 'coverage',
      label: 'Coverage',
      specialCell: (row: CoveredItemRow): React.ReactNode =>
        row.coverage === EXCLUDED
          ? 'Excluded'
          : row.coverage !== NO_COVERAGE
            ? coverages?.find(({ value }) => value === row.coverage)?.description
            : '-',
      specialCmpFunc: (a: CoveredItemRow, b: CoveredItemRow) =>
        a.coverage === EXCLUDED ? 1 : stringCmp(a.coverage, b.coverage),
    },
    {
      id: 'covered',
      label: 'Covered',
      specialCell: (row: CoveredItemRow) =>
        row.amounts.covered !== undefined ? <AmountSpecialCell amount={row.amounts.covered} /> : '',
    },
    {
      id: 'not_covered',
      label: 'Not Covered',
      specialCell: (row: CoveredItemRow) => {
        if (row.coverage === EXCLUDED) {
          return undefined;
        }
        const totalAmount = COVERAGE_DECISIONS.filter(({ value }) => value !== 'covered').reduce(
          (sum, coverageDecision) => {
            return sum + (row.amounts[coverageDecision.value] ?? 0);
          },
          0
        );

        return <AmountSpecialCell amount={totalAmount} />;
      },
    },
    ...coInsuranceColumns,
    {
      id: 'undecided',
      label: 'Undecided',
      specialCell: (row: CoveredItemRow) =>
        row.amounts[UNDECIDED] !== undefined ? <AmountSpecialCell amount={row.amounts[UNDECIDED]} /> : <></>,
    },
    {
      id: 'action',
      label: 'Action',
      specialCell: (row: CoveredItemRow) =>
        !isEmpty(row.coverage) && !!row.coverage && row.coverage !== NO_COVERAGE && row.coverage !== EXCLUDED ? (
          <ApplyButton
            row={row}
            onApply={async (...args) => {
              mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_INVOICE_PAGE_COVERAGE_APPLY_CLICKED);
              const saveSuccess = await onSave({
                line_items: currentLineItems,
                coverages: aggregateCoverages,
                invoice_data: editedInvoiceFields,
              });
              if (saveSuccess && onApplyCoveredItem) {
                await onApplyCoveredItem(...args);
              }
            }}
            coverageDecisionMap={coverageDecisionMap}
            exposure={exposuresByCoverageKey[row.coverage]}
            disabled={disabled}
            saveDisabledMessage={saveDisabledMessage}
          />
        ) : (
          <></>
        ),
    },
  ];

  const renderDialogContent = () => (
    <CardDialog
      title="Invoice Analysis"
      isDialog
      onClose={onClose}
      fullScreen
      hideTitleContainer
      open={open}
      containerClassName="first:p-0 bg-slate-100"
      footerActions={
        !isLoading && !isError ? (
          <Footer
            aggregateCoverages={aggregateCoverages}
            disabled={disabled}
            submitting={submitting}
            onClose={onClose}
            onSave={onSave}
            onSaveAndFinish={onSaveAndFinish}
            lineItems={currentLineItems}
            invoiceData={editedInvoiceFields}
            saveDisabledMessage={saveDisabledMessage}
          />
        ) : undefined
      }
    >
      {dialogTitle && <Header dialogTitle={dialogTitle} />}
      <div>
        <Heading variant={Heading.TYPES.H1} className="p-20 pb-0">
          <IconButton
            disabled={submitting}
            onClick={() => {
              mixpanel.track(CLIVE_MIXPANEL_EVENTS.ACTION_INVOICE_PAGE_BACK_CLICKED);

              onClose();
            }}
          >
            <ArrowDownwardIcon className="rotate-[90deg]" size={20} />
          </IconButton>
          {customMainTitle ||
            `Invoice ${invoiceData.document_claim_internal_id ? `#${currentInvoiceData.document_claim_internal_id}` : ''} Payment`}
        </Heading>
      </div>
      {isLoading || isError ? (
        <div className="p-20">
          <SkeletonTable rowsCount={5} columnsCount={7} isError={isError} />
        </div>
      ) : (
        <div className="p-20">
          {shouldShowInvoiceSummary && (
            <Summary
              invoiceData={currentInvoiceData}
              onUpdate={(field, value) => {
                setCurrentInvoiceData((prev) => ({
                  ...prev,
                  [field]: value,
                }));
                setEditedInvoiceFields((prev) => ({
                  ...prev,
                  [field]: value,
                }));
              }}
              disabled={disabled}
              shouldShowDatePerLineItem={shouldShowDatePerLineItem}
            />
          )}
          <div className="mb-20 inline-flex w-full gap-20">
            {filePath ? (
              <PanelGroup direction="horizontal" id="group">
                <Panel id="table-display" defaultSize={70}>
                  <LineItemsTable
                    lineItemsColumns={lineItemsColumns}
                    currentLineItems={currentLineItems}
                    shouldShowAddItemButton={shouldShowAddItemButton}
                    disabled={disabled}
                    setCurrentLineItems={setCurrentLineItems}
                    onLineItemClick={onLineItemClick}
                    handleLineItemClick={handleLineItemClick}
                    shouldShowAmountSummary={shouldShowLineItemsAmountSummary}
                    summaryItems={effectiveSummaryItems}
                    policyCurrency={policyCurrency}
                    customButtons={resolvedCustomButtons}
                    lineItemsSectionTitle={lineItemsSectionTitle}
                  />
                </Panel>
                <PanelResizeHandle id="resize-handle" className="mx-12 w-2 rounded-lg bg-slate-500" />
                <Panel
                  id="file-display"
                  className={`relative flex-1 rounded-lg ${shouldShowCoverageSummary ? 'min-h-40' : 'min-h-[75vh]'}`}
                >
                  {onCloseFilePath && (
                    <IconButton
                      onClick={onCloseFilePath}
                      aria-label="Close document"
                      disabled={submitting}
                      className="shadow absolute right-8 top-8 z-10 bg-white"
                    >
                      <CloseIcon size={16} />
                    </IconButton>
                  )}
                  <object
                    className={`${isFileLoaded ? 'h-full min-h-[40vh] w-full' : ''}`}
                    data={filePath}
                    onLoad={() => setIsFileLoaded(true)}
                  />
                </Panel>
              </PanelGroup>
            ) : (
              <LineItemsTable
                lineItemsColumns={lineItemsColumns}
                currentLineItems={currentLineItems}
                shouldShowAddItemButton={shouldShowAddItemButton}
                disabled={disabled}
                setCurrentLineItems={setCurrentLineItems}
                onLineItemClick={onLineItemClick}
                handleLineItemClick={handleLineItemClick}
                shouldShowAmountSummary={shouldShowLineItemsAmountSummary}
                summaryItems={effectiveSummaryItems}
                policyCurrency={policyCurrency}
                customButtons={resolvedCustomButtons}
                lineItemsSectionTitle={lineItemsSectionTitle}
              />
            )}
          </div>
          {shouldShowCoverageSummary && (
            <div className="mb-32 rounded-lg bg-white p-32 pt-20">
              <Heading variant={Heading.TYPES.H1} className="mb-8">
                Coverage Summary
              </Heading>
              <AlertBanner
                note="Payments can only be made for coverages with no reserve or previous payments. You can make a payment using the Coverages & Invoice Summary screen."
                alertType={AlertBanner.ALERT_TYPES.INFO}
                withIcon
                className="mb-8"
              />
              <SortableTable columns={coverageSummaryColumns} rows={aggregateCoverages} stickyHeader />
            </div>
          )}
        </div>
      )}
    </CardDialog>
  );

  // If multi-currency is enabled and invoice has a currency, wrap in currency formatter
  return renderDialogContent();
};
